import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpEventType} from '@angular/common/http';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogModule} from 'primeng/dialog';
import {ButtonDirective, ButtonLabel} from 'primeng/button';
import {DialogService} from 'primeng/dynamicdialog';
import {TagModule} from 'primeng/tag';
import {Subject, of} from 'rxjs';
import {catchError, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {CropPeriodService, AgrotechnicalMeasureService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {
  CustomFieldViewModel,
  Field,
  FieldService,
  ReportService,
  Season,
  SeasonService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {AnomalyPolygonsService} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {ModalFormEditAllComponent} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-form-edit-all/modal-form-edit-all.component';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {FilterOperator} from '@app/utils/filter-operator';
import {Loading} from '@components/ui/loading/loading';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {environment} from '@root/src/environments/environment';
import {IndexDataManager} from '@app/pages/electronic-field-map/pages/fields/passport/forms/crop-chart-index/managers/index-data-manager';
import {NormDataManager} from '@app/pages/electronic-field-map/pages/fields/passport/forms/crop-chart-index/managers/norm-data-manager';
import {NotReadyApiService} from '@app/api/not-ready-api-service';
import {ZipExtractor} from '@app/pages/crop-monitoring/components/request-result-modal/utils/zip-extractor.util';
import {
  resolveAnomalyPolygonColor,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';
import {FieldCropsColorPipe} from '@app/utils/pipes/field-crops-color-pipe';
import {INDEX_LAYER_OPTIONS, IndexLayerOption} from '@shared/data/index-layer-options-data';
import {handleErrors} from '@app/utils/_msf';
import {IndexManager} from '@components/openstreetmap/managers/index.manager';
import {dateToYmd, formatDateLabelShortYear, ymdFromApiDate} from '../../managers/field-details-date.util';
import {
  buildIndexLowSignalYmdSet,
  buildFieldDetailsLineChartOptions,
  filterIndexChartSeriesForPlot,
  interpolateSeriesToLabels,
} from '../../managers/field-details-chart.util';
import {wmsStyleForIndex} from '../../managers/field-details-wms.util';
import {normalizePolygonAsWkt} from '../../managers/field-details-anomaly-geometry.util';
import {INDEX_TO_VEGETATION_TYPE} from '../../managers/field-details-index-vegetation.map';
import {buildMapIndexScaleBarGradient} from '../../managers/field-details-index-legend.util';
import {FieldDetailsTimelineSectionComponent} from '../field-details-timeline-section/field-details-timeline-section.component';
import {FieldDetailsIndicesMapChartRowComponent} from '../field-details-indices-map-chart-row/field-details-indices-map-chart-row.component';
import {FieldDetailsIndicesBottomGridComponent} from '../field-details-indices-bottom-grid/field-details-indices-bottom-grid.component';
import {FieldDetailsSatelliteTimelineService} from '../../managers/field-details-satellite-timeline.service';
import {FieldDetailsRecommendationsService} from '../../managers/field-details-recommendations.service';
import {
  buildRecommendationsForAnomalyRows,
  extractAnomalyCodesFromRows,
  FieldDetailsRecommendationView,
  resolveAnomalyTypeCode,
} from '../../managers/field-details-recommendations.util';
import {
  buildFieldPhotoBidsQuery,
  clampPhotoSliderIndex,
  extractZipImageGalleryItems,
  fieldPhotosHttpProgressPercent,
  fieldPhotosZipExtractOverlayPercent,
  FIELD_PHOTOS_EMPTY_GUID,
  pickLatestFieldPhotoBidFileResultId,
  resolveFieldDetailsDataUpdatedDisplay,
} from '../../managers/field-details-photos.util';
import type {CultureRow} from '../../field-details-dialog.types';

@Component({
  selector: 'app-field-details-indices-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonDirective,
    ButtonLabel,
    TagModule,
    Loading,
    SecondaryButton,
    PrimaryButton,
    FormBuilder,
    FieldDetailsTimelineSectionComponent,
    FieldDetailsIndicesMapChartRowComponent,
    FieldDetailsIndicesBottomGridComponent,
  ],
  providers: [DialogService, DatePipe, FieldCropsColorPipe],
  templateUrl: './field-details-indices-tab.component.html',
  styleUrls: ['./field-details-indices-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsIndicesTabComponent implements OnInit, OnDestroy, OnChanges {
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly reportService = inject(ReportService);
  private readonly seasonService = inject(SeasonService);
  private readonly anomalyService = inject(AnomalyPolygonsService);
  private readonly cropPeriodService = inject(CropPeriodService);
  private readonly agroMeasureService = inject(AgrotechnicalMeasureService);
  private readonly cropService = inject(CropService);
  private readonly bidService = inject(BidService);
  private readonly notReadyApi = inject(NotReadyApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly datePipe = inject(DatePipe);
  private readonly fieldCropsColorPipe = inject(FieldCropsColorPipe);
  private readonly indexManager = inject(IndexManager);
  private readonly satelliteTimeline = inject(FieldDetailsSatelliteTimelineService);
  private readonly fieldDetailsRecommendations = inject(FieldDetailsRecommendationsService);
  private readonly destroy$ = new Subject<void>();

  /** Chart series cache key: `${index}::${cropId}`. */
  private readonly chartSeriesCache = new Map<
    string,
    { chartData: any; chartIndexLowSignalYmd: Set<string> }
  >();

  @Input({ required: true }) field!: Field | null | any;
  @Input({ required: true }) customField!: CustomFieldViewModel & { seasons: Season[] } | null;
  @Input({ required: true }) fieldPolygonJson!: string | null;
  @Input({ required: true }) fieldCenter!: { lat: number; lng: number } | null;
  @Input({ required: true }) mapCenter!: [number, number];
  @Input() dataUpdatedAt: Date | null = null;
  @Input() regionPathDisplay: string | null = null;
  @Output() reloadFieldRequest = new EventEmitter<void>();
  cultureRows: CultureRow[] = [];
  selectedCropId: string | null = null;
  /**
   * Active seasons for the field, loaded once per field from
   * `/cropMonitoring/Season/GetActiveSeasonByDate` (date omitted → server "now").
   * Replaces the former use of the full `customField.seasons` list for the crop/season selects.
   */
  private activeSeasons: Season[] = [];
  /** Crops resolved for active seasons (customField + dictionary fallback). */
  private activeCropsById = new Map<string, CultureRow['crop']>();


  satelliteProducts: any[] = [];
  timelineDates: Date[] = [];
  /** Satellite date list loading (satellite API then index-report fallback). */
  timelineDatesLoading = false;
  selectedTimelineDate: Date | null = null;
  calendarDialogVisible = false;


  anomaliesByDay = new Set<string>();
  /** All anomaly rows for the season, grouped by `YYYY-MM-DD` (single CustomGetAnomalyPolygons load). */
  private anomaliesByDayRows = new Map<string, any[]>();
  /** From chart only: YYYY-MM-DD where raw index < 0.1 (incl. negatives). */
  private chartIndexLowSignalYmd = new Set<string>();
  /** Days from GetAllProductsByPoint with cloudCover > 50% → grey dot on timeline. */
  highCloudCoverByDay = new Set<string>();
  anomaliesForSelectedDay: any[] = [];
  anomaliesLoading = false;
  /** FieldAnomaly dictionary rows indexed by {@code codeFromEnum}. */
  private fieldAnomalyByCode = new Map<number, unknown>();
  recommendationsForSelectedDay: FieldDetailsRecommendationView[] = [];
  focusedRecommendationCode: number | null = null;

  indexOptions: IndexLayerOption[] = INDEX_LAYER_OPTIONS.filter(o => o.value);
  selectedIndexValue: string = 'Ndvi';

  /** XYZ tile URL template ({x}/{y}/{z}) for the index raster overlay; null hides it. */
  indexTileUrl: string | null = null;
  /** True while resolving the GeoServer layer name (index or timeline date change). */
  wmsTileLoading = false;
  /** Bumped on every {@link revokeWms} and each started fetch; stale callbacks ignore results. */
  private wmsLoadSeq = 0;
  /** Opacity of the index tile overlay on the mini-map (0–1). */
  wmsOverlayOpacity = 0.75;
  anomalyMapPolygonJson: string | null = null;
  anomalyMapPolygonColor: string | null = null;

  chartData: any = null;
  chartOptions: any = null;
  chartLoading = false;
  private indexDataManager = new IndexDataManager(this.notReadyApi);
  private normDataManager = new NormDataManager(this.cropService);

  cropPhases: any[] = [];
  agroMeasures: any[] = [];

  block1FormModel: { cropId: string | null; seasonId: string | null } = { cropId: null, seasonId: null };
  /** Season form row (replaces former cadastre slot; icon row). */
  seasonFormFields: IMsfFormBuilder[] = [];
  /** Crop / variety form block. */
  cropFormFields: IMsfFormBuilder[] = [];
  /** User-picked season (overrides crop → season match). */
  selectedSeasonId: string | null = null;

  calendarFormModel: { date: Date | string | null } = { date: null };
  calendarFormFields: IMsfFormBuilder[] = [];

  detailActiveTab = 'reco';

  photoStrip: { src: string; name: string }[] = [];
  photosTotal = 0;
  photoGalleryUrls: string[] = [];
  photoSliderVisible = false;
  photoSliderIndex = 0;
  photoSliderImages: string[] = [];
  private photoBlobUrls = new Map<string, string>();

  /** Photo strip: loads ZIP from last finished bid linked to the field. */
  fieldPhotosLoading = false;
  fieldPhotosProgress = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customField']?.currentValue) {
      this.loadActiveSeasons();
    }
  }

  /**
   * Loads the field's active seasons via `GetActiveSeasonByDate`, then rebuilds the crop/season
   * selects and the season-scoped data. The earlier client-side derivation from
   * `customField.seasons` has been removed.
   */
  private loadActiveSeasons(): void {
    const fieldId = this.fieldEntity?.id ?? this.field?.id;
    if (!fieldId) {
      this.activeSeasons = [];
      this.activeCropsById.clear();
      this.buildCultureRows();
      this.reloadSeasonScopedData();
      return;
    }
    this.seasonService
      .cropMonitoringSeasonGetActiveSeasonByDateGet(fieldId)
      .pipe(
        switchMap((seasons) => {
          this.activeSeasons = seasons || [];
          this.seedActiveCropsFromCustomField();
          const missingIds = this.getMissingActiveCropIds();
          if (!missingIds.length) {
            return of(null);
          }
          return this.cropService.dictionariesCropGetByQueryPost({
            filters: [
              {
                filterField: 'id',
                filterOperator: FilterOperator.AnyOf,
                filterValue: missingIds,
              },
            ],
          } as any).pipe(catchError(() => of([])));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((loadedCrops) => {
        (loadedCrops || []).forEach((crop) => {
          if (crop?.id) {
            this.activeCropsById.set(crop.id, crop as CultureRow['crop']);
          }
        });
        this.buildCultureRows();
        this.reloadSeasonScopedData();
        this.cdr.markForCheck();
      });
  }

  private seedActiveCropsFromCustomField(): void {
    this.activeCropsById.clear();
    ((this.customField?.crops || []) as CultureRow['crop'][]).forEach((crop) => {
      if (crop?.id) {
        this.activeCropsById.set(crop.id, crop);
      }
    });
  }

  private getMissingActiveCropIds(): string[] {
    const ids = new Set<string>();
    for (const season of this.activeSeasons) {
      if (season.cropId && !this.activeCropsById.has(season.cropId)) {
        ids.add(season.cropId);
      }
    }
    return [...ids];
  }

  private resolveActiveCrop(cropId: string | null | undefined): CultureRow['crop'] | undefined {
    if (!cropId) {
      return undefined;
    }
    return this.activeCropsById.get(cropId);
  }

  ngOnInit(): void {
    this.initChartOptionsShell();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initChartOptionsShell();
      this.syncRecommendationsForSelectedDay();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeWms();
    this.cleanupPhotoBlobs();
    this.clearAnomalyMapHighlight();
  }

  get fieldEntity(): any {
    return this.field?.field ?? this.field;
  }

  get cadastralNumber(): string {
    const e = this.fieldEntity;
    const raw =
      e?.cadastralNumber ??
      e?.cadastralNum ??
      e?.cadastral ??
      e?.cadNumber ??
      e?.cadastralNumberValue;
    if (raw == null || String(raw).trim() === '') {
      return '';
    }
    return String(raw).trim();
  }

  /** Read-only varieties for the selected crop. */
  get selectedVarietiesDisplay(): string {
    if (!this.selectedCropId || !this.cultureRows?.length) {
      return '';
    }
    const row = this.cultureRows.find(r => r.crop.id === this.selectedCropId);
    const vars = row?.varieties || [];
    if (!vars.length) {
      return '';
    }
    const lang = this.translate.currentLang || 'ru-RU';
    return vars
      .map((v: any) => {
        const n = v?.names?.[lang] || v?.names?.['ru-RU'] || v?.names?.['en-US'] || v?.name;
        return n != null && String(n).trim() ? String(n).trim() : '';
      })
      .filter(Boolean)
      .join(', ');
  }

  get fieldDisplayName(): string {
    return this.fieldEntity?.name || this.field?.fieldName || this.field?.name || '';
  }

  get fieldRegionDisplay(): string {
    if (this.regionPathDisplay != null && String(this.regionPathDisplay).trim() !== '') {
      return this.regionPathDisplay;
    }
    return this.flatEmbeddedRegionLabel();
  }

  private flatEmbeddedRegionLabel(): string {
    const r = (this.customField as any)?.region;
    if (!r) {
      return '—';
    }
    if (typeof r === 'string') {
      return r;
    }
    const lang = this.translate.currentLang || 'ru-RU';
    return (
      r.names?.[lang] ||
      r.names?.['ru-RU'] ||
      r.names?.['en-US'] ||
      r.name ||
      r.regionName ||
      '—'
    );
  }

  get agricultureProducerDisplay(): string {
    const v = (this.customField as any)?.agricultureProducerName;
    return v != null && String(v).trim() ? String(v) : '—';
  }

  get fieldAreaHa(): number {
    return this.fieldEntity?.fieldArea ?? this.field?.fieldArea ?? 0;
  }

  get lastSatelliteImageLabel(): string {
    if (!this.satelliteProducts?.length) {
      return '—';
    }
    const sorted = [...this.satelliteProducts].sort((a, b) =>
      String(b.originDate || '').localeCompare(String(a.originDate || ''))
    );
    const d = sorted[0]?.originDate;
    return d ? (this.datePipe.transform(d, 'dd.MM.yyyy') ?? '—') : '—';
  }

  get selectedSeason(): Season | undefined {
    const seasons = this.activeSeasons || [];
    if (this.selectedSeasonId) {
      const byId = seasons.find(s => s.id === this.selectedSeasonId);
      if (byId) {
        return byId;
      }
    }
    if (this.selectedCropId) {
      return seasons.find(s => s.cropId === this.selectedCropId);
    }
    return seasons[0];
  }

  formatSeasonDatePair(plan: unknown, fact: unknown): string {
    const p = plan ? this.datePipe.transform(plan as Date | string, 'dd.MM.yyyy') : '—';
    const f = fact ? this.datePipe.transform(fact as Date | string, 'dd.MM.yyyy') : '—';
    return `${p} / ${f}`;
  }

  get plantingBoundaryDate(): Date | null {
    const s = this.selectedSeason;
    const raw = s?.plantingDate || s?.plantingDatePlan;
    if (!raw) {
      return null;
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  get plantingBoundaryYmd(): string | null {
    const d = this.plantingBoundaryDate;
    return d ? dateToYmd(d) : null;
  }

  /**
   * Timeline lower bound: 10 days before planting (plan or fact) so pre-sowing index dates show.
   * Falls back to {@link plantingBoundaryYmd} when no planting date exists.
   */
  get timelineStartYmd(): string | null {
    const d = this.plantingBoundaryDate;
    if (!d) {
      return null;
    }
    const shifted = new Date(d);
    shifted.setDate(shifted.getDate() - 10);
    return dateToYmd(shifted);
  }

  get activeSeasonsOptionsForReport(): any[] {
    return (this.activeSeasons || []).map(season => {
      const crop = this.resolveActiveCrop(season.cropId);
      return {...(crop || {}), id: season.id};
    });
  }

  get mapSeasonPolygonJson(): string | null {
    if (this.anomalyMapPolygonJson) {
      return this.anomalyMapPolygonJson;
    }
    const pj = this.selectedSeason?.polygonJson as string | undefined | null;
    return pj && String(pj).trim() ? String(pj) : null;
  }

  get mapSeasonPolygonColor(): string {
    const crop = this.resolveActiveCrop(this.selectedCropId) as any;
    return this.fieldCropsColorPipe.transform(crop);
  }

  /**
   * Season polygon fill colour. The index raster is now a real XYZ tile layer rendered *below* the
   * vector polygons, so the polygon keeps its (semi-transparent) crop fill instead of being emptied.
   */
  get mapPolygonColorOnMiniMap(): string {
    if (this.anomalyMapPolygonJson && this.anomalyMapPolygonColor) {
      return this.anomalyMapPolygonColor;
    }
    return this.mapSeasonPolygonColor;
  }

  onWmsOpacitySliderChange(): void {
    this.cdr.markForCheck();
  }

  onMapChartWmsOpacity(value: number): void {
    this.wmsOverlayOpacity = value;
    this.onWmsOpacitySliderChange();
  }

  get fieldStatusBadge(): { severity: 'success' | 'danger' | 'warn'; labelKey: string } {
    if (this.anomaliesForSelectedDay?.length) {
      return { severity: 'danger', labelKey: 'FIELD_DETAILS_V2.STATUS_ANOMALY' };
    }
    return { severity: 'success', labelKey: 'FIELD_DETAILS_V2.STATUS_NORMAL' };
  }

  /**
   * Date shown next to "Data updated": last bid date from API when present, else selected satellite
   * timeline date, else last successful field load time.
   */
  get dataUpdatedDisplay(): Date | null {
    return resolveFieldDetailsDataUpdatedDisplay({
      lastBidDateRaw: (this.customField as { lastBidDate?: string | Date | null } | null)?.lastBidDate,
      selectedTimelineDate: this.selectedTimelineDate,
      loadedAt: this.dataUpdatedAt,
    });
  }

  showAnomalyOnMap(row: any): void {
    const raw = row?.anomalyPolygon?.polygonJson ?? row?.polygonJson;
    const normalized = normalizePolygonAsWkt(raw);
    if (!normalized) {
      return;
    }
    this.anomalyMapPolygonJson = normalized;
    this.anomalyMapPolygonColor = resolveAnomalyPolygonColor(row);
    const code = resolveAnomalyTypeCode(row);
    if (code != null) {
      this.detailActiveTab = 'reco';
      this.focusedRecommendationCode = code;
    }
    this.cdr.markForCheck();
  }

  /** Drop anomaly footprint on the mini-map (season polygon returns when cleared). */
  private clearAnomalyMapHighlight(): void {
    this.anomalyMapPolygonJson = null;
    this.anomalyMapPolygonColor = null;
  }

  onCultureChange(): void {
    this.chartSeriesCache.clear();
    this.clearAnomalyMapHighlight();
    this.reloadSeasonScopedData();
  }

  onBlock1ModelChange(model: { cropId?: string | null; seasonId?: string | null }): void {
    let reload = false;
    const newSid = model?.seasonId ?? this.selectedSeasonId;
    const newCid = model?.cropId ?? this.selectedCropId;

    if (newSid && newSid !== this.selectedSeasonId) {
      this.selectedSeasonId = newSid;
      const se = (this.activeSeasons || []).find(s => s.id === newSid);
      if (se?.cropId && se.cropId !== this.selectedCropId) {
        this.selectedCropId = se.cropId;
      }
      reload = true;
    } else if (newCid && newCid !== this.selectedCropId) {
      this.selectedCropId = newCid;
      const se = (this.activeSeasons || []).find(s => s.cropId === newCid);
      this.selectedSeasonId = se?.id ?? this.selectedSeasonId;
      reload = true;
    }

    if (reload) {
      this.block1FormModel = { cropId: this.selectedCropId, seasonId: this.selectedSeasonId };
      this.onCultureChange();
    }
  }

  getReportFormFields(): IMsfFormBuilder[] {
    return [
      {
        name: 'seasonId',
        title: this.translate.instant('Culture') || 'Culture',
        type: 'select',
        placeholder: this.translate.instant('Выберите культуру'),
        selectOptions: this.activeSeasonsOptionsForReport,
        required: true,
      },
      {
        name: 'reportDate',
        title: this.translate.instant('Date') || 'Date',
        type: 'date',
        required: true,
        options: {
          date: {
            dateOnly: true,
          },
        },
      },
      {
        name: 'locale',
        title: this.translate.instant('Language') || 'Language',
        type: 'select',
        placeholder: this.translate.instant('Выберите язык'),
        required: true,
        selectOptions: [
          { id: 'ru-RU', name: 'Русский' },
          { id: 'en-US', name: 'English' },
          { id: 'uz-Latn-UZ', name: "O'zbek" },
        ],
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            translateBy: 'none',
          },
        },
      },
    ];
  }

  openReportModal(): void {
    const fieldId = this.fieldEntity?.id || this.field?.id;
    const season = this.selectedSeason;
    if (!fieldId || !season?.id) {
      return;
    }
    const currentLocale = this.translate.currentLang || 'ru-RU';
    const reportDay = this.selectedTimelineDate ? dateToYmd(this.selectedTimelineDate) : dateToYmd(new Date());
    const defaultModel = {
      fieldId,
      seasonId: season.id,
      reportDate: reportDay,
      locale: currentLocale,
      alsoPdf: true,
    };
    const ref = this.dialogService.open(ModalFormEditAllComponent, {
      header: this.translate.instant('FIELD_DETAILS_V2.GENERATE_REPORT'),
      width: '40%',
      closable: true,
      modal: true,
      data: {
        model: defaultModel,
        service: this.reportService,
        fields: this.getReportFormFields(),
        fieldId,
        saveMethod: 'cropMonitoringReportBuildFarmerReportPost',
        createMethod: 'cropMonitoringReportBuildFarmerReportPost',
      },
    });
    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result?.success) {
        this.reloadFieldRequest.emit();
      }
    });
  }

  onTimelinePick(d: Date): void {
    this.selectedTimelineDate = d;
    this.clearAnomalyMapHighlight();
    this.focusedRecommendationCode = null;
    this.refreshAnomaliesForDay();
    this.refreshWmsOverlay();
    this.cdr.markForCheck();
  }

  openCalendarDialog(): void {
    this.rebuildCalendarFormFields();
    this.calendarFormModel = { date: this.selectedTimelineDate ?? this.startOfToday() };
    this.calendarDialogVisible = true;
    this.cdr.markForCheck();
  }

  closeCalendarDialog(): void {
    this.calendarDialogVisible = false;
    this.cdr.markForCheck();
  }

  applyCalendarSelection(): void {
    const raw = this.calendarFormModel?.date ?? null;
    if (raw == null || raw === '') {
      return;
    }
    const d = raw instanceof Date ? new Date(raw.getTime()) : new Date(String(raw));
    if (isNaN(d.getTime())) {
      return;
    }
    d.setHours(0, 0, 0, 0);
    const ymd = dateToYmd(d);
    if (this.timelineDates.length) {
      const match = this.timelineDates.find(x => dateToYmd(x) === ymd);
      if (match) {
        this.onTimelinePick(match);
        this.closeCalendarDialog();
      }
    } else {
      this.onTimelinePick(d);
      this.closeCalendarDialog();
    }
  }

  private rebuildCalendarFormFields(): void {
    const restrict = this.timelineDates.length > 0;
    this.calendarFormFields = [
      {
        name: 'date',
        title: this.translate.instant('Select date'),
        type: 'date',
        required: false,
        options: {
          date: {
            dateOnly: true,
            allowOnlySelectedDate: restrict,
            ...(restrict
              ? {
                  /** `yyyy-MM-dd` strings for app-date-picker / isAllowed. */
                  selectableDates: (): string[] => this.timelineDates.map(d => dateToYmd(d)),
                }
              : {}),
          },
        },
      },
    ];
  }

  private startOfToday(): Date {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  selectIndex(idx: string): void {
    this.selectedIndexValue = idx;
    this.reloadChart(true);
    this.refreshWmsOverlay();
    this.cdr.markForCheck();
  }

  /** CSS gradient for the vertical index scale bar next to the WMS preview. */
  get mapIndexScaleBarGradient(): string {
    return buildMapIndexScaleBarGradient(this.selectedIndexValue);
  }

  openPhotoSlider(i: number): void {
    const urls = this.photoGalleryUrls.filter(Boolean);
    if (!urls.length) {
      return;
    }
    this.photoSliderImages = [...urls];
    this.photoSliderIndex = clampPhotoSliderIndex(i, this.photoSliderImages.length);
    this.photoSliderVisible = true;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /** Opens slider on the first thumbnail not shown in the strip (strip shows first 3). */
  openPhotoSliderFromMore(): void {
    this.openPhotoSlider(3);
  }

  closePhotoSlider(): void {
    this.photoSliderVisible = false;
    this.cdr.markForCheck();
  }

  advancePhotoSlider(delta: number): void {
    const n = this.photoSliderImages.length;
    if (!n) {
      return;
    }
    this.photoSliderIndex = (this.photoSliderIndex + delta + n) % n;
    this.cdr.markForCheck();
  }

  private buildCultureRows(): void {
    if (!this.customField) {
      this.cultureRows = [];
      this.selectedCropId = null;
      this.selectedSeasonId = null;
      return;
    }
    const seenCropIds = new Set<string>();
    this.cultureRows = [];
    for (const season of this.activeSeasons) {
      const cropId = season.cropId;
      if (!cropId || seenCropIds.has(cropId)) {
        continue;
      }
      const crop = this.resolveActiveCrop(cropId);
      if (!crop) {
        continue;
      }
      seenCropIds.add(cropId);
      const varieties = (this.customField?.varieties || []).filter((v: any) => v.cropId === cropId);
      this.cultureRows.push({crop, season, varieties});
    }
    if (!this.selectedCropId && this.cultureRows.length) {
      this.selectedCropId = this.cultureRows[0].crop.id;
    }
    const seasons = this.activeSeasons || [];
    let byCurrentId =
      this.selectedSeasonId && seasons.some(s => s.id === this.selectedSeasonId)
        ? seasons.find(s => s.id === this.selectedSeasonId)
        : undefined;
    if (byCurrentId && this.selectedCropId && byCurrentId.cropId !== this.selectedCropId) {
      byCurrentId = undefined;
    }
    const byCrop =
      this.selectedCropId != null ? seasons.find(s => s.cropId === this.selectedCropId) : undefined;
    const fallback = seasons[0];
    const picked = byCurrentId || byCrop || fallback;
    this.selectedSeasonId = picked?.id ?? null;
    this.rebuildBlock1FormFields();
  }

  private rebuildBlock1FormFields(): void {
    const seasons = this.activeSeasons || [];
    const lang = this.translate.currentLang || 'ru-RU';

    this.seasonFormFields =
      seasons.length > 0
        ? [
            {
              name: 'seasonId',
              title: this.translate.instant('FIELD_DETAILS_V2.SEASON_SELECT'),
              type: 'select',
              placeholder: this.translate.instant('Select'),
              required: false,
              selectOptions: seasons.map(s => {
                const crop = this.resolveActiveCrop(s.cropId) as any;
                const cropLabel =
                  crop?.names?.[lang] ||
                  crop?.names?.['ru-RU'] ||
                  crop?.names?.['en-US'] ||
                  crop?.name ||
                  '—';
                const y = s.plantingDatePlan
                  ? this.datePipe.transform(s.plantingDatePlan, 'yyyy')
                  : '';
                return { id: s.id, label: y ? `${cropLabel} · ${y}` : cropLabel };
              }),
              options: {
                select: {
                  optionLabel: 'label',
                  optionValue: 'id',
                  translateBy: 'none',
                },
              },
            },
          ]
        : [];

    this.cropFormFields = [
      {
        name: 'cropId',
        title: this.translate.instant('FIELD_DETAILS_V2.CROP_SELECT'),
        type: 'select',
        placeholder: this.translate.instant('Select'),
        required: false,
        selectOptions: this.cultureRows.map(r => ({ ...r.crop, id: r.crop.id })),
        options: {
          select: {
            optionLabel: 'names',
            optionValue: 'id',
            translateBy: 'names',
          },
        },
      },
    ];
    this.block1FormModel = { cropId: this.selectedCropId, seasonId: this.selectedSeasonId };
  }

  private reloadSeasonScopedData(): void {
    this.revokeWms();
    this.loadSatelliteProducts();
    this.loadAnomaliesRange();
    this.loadCropPhases();
    this.loadAgroMeasures();
    this.reloadChart(true);
    this.loadFieldPhotosFromLastFinishedBid();
  }

  private loadSatelliteProducts(): void {
    if (!this.fieldCenter) {
      this.satelliteProducts = [];
      this.timelineDates = [];
      this.highCloudCoverByDay = new Set();
      this.selectedTimelineDate = this.startOfToday();
      this.timelineDatesLoading = false;
      return;
    }
    this.timelineDatesLoading = true;
    this.cdr.markForCheck();
    const endTimelineLoading = (): void => {
      this.timelineDatesLoading = false;
      this.cdr.markForCheck();
    };
    const { lat, lng } = this.fieldCenter;
    const start = this.timelineStartYmd;
    this.satelliteTimeline
      .fetchProductsByPoint(lat, lng)
      .pipe(takeUntil(this.destroy$))
      .subscribe((rows: any[]) => {
        const filtered = this.satelliteTimeline.applyPlantingFilter(rows, start);
        this.satelliteProducts = filtered;
        this.highCloudCoverByDay = this.satelliteTimeline.highCloudCoverDaysFromProducts(filtered);
        this.timelineDates = this.satelliteTimeline.timelineDatesFromProducts(filtered);
        if (this.timelineDates.length) {
          this.alignTimelineSelectionAfterDatesLoaded();
          this.reloadAnomaliesIfNoPlantingDate();
          this.refreshAnomaliesForDay();
          this.refreshWmsOverlay();
          endTimelineLoading();
        } else if (this.selectedSeason?.id) {
          this.satelliteTimeline
            .loadFallbackTimelineDates$(this.selectedSeason.id, start)
            .pipe(
              takeUntil(this.destroy$),
              finalize(() => endTimelineLoading()),
            )
            .subscribe(dates => {
              this.timelineDates = dates;
              this.highCloudCoverByDay = new Set();
              this.alignTimelineSelectionAfterDatesLoaded();
              this.reloadAnomaliesIfNoPlantingDate();
              this.refreshAnomaliesForDay();
              this.refreshWmsOverlay();
              this.cdr.markForCheck();
            });
        } else {
          this.alignTimelineSelectionAfterDatesLoaded();
          this.refreshAnomaliesForDay();
          this.refreshWmsOverlay();
          endTimelineLoading();
        }
      });
  }

  private alignTimelineSelectionAfterDatesLoaded(): void {
    if (!this.timelineDates.length) {
      this.selectedTimelineDate = this.startOfToday();
      return;
    }
    if (!this.selectedTimelineDate) {
      this.selectedTimelineDate = this.timelineDates[this.timelineDates.length - 1];
      return;
    }
    const y = dateToYmd(this.selectedTimelineDate);
    const found = this.timelineDates.find(d => dateToYmd(d) === y);
    this.selectedTimelineDate = found ?? this.timelineDates[this.timelineDates.length - 1];
  }

  /**
   * Lower bound for the anomaly query.
   *
   * Uses the planting date (plan or fact) when available; otherwise falls back to the earliest
   * satellite product day from {@link FieldDetailsSatelliteTimelineService.fetchProductsByPoint}
   * so anomalies still load (and show red on the timeline) for seasons without a planting date.
   */
  private get anomalyRangeStartYmd(): string | null {
    const plant = this.plantingBoundaryYmd;
    if (plant) {
      return plant;
    }
    return this.timelineDates.length ? dateToYmd(this.timelineDates[0]) : null;
  }

  /**
   * Re-runs the anomaly load once the timeline is built when the season has no planting date,
   * so the fallback lower bound ({@link anomalyRangeStartYmd}) can use the earliest product day.
   * No-op when a planting date exists (already loaded by {@link reloadSeasonScopedData}).
   */
  private reloadAnomaliesIfNoPlantingDate(): void {
    if (this.plantingBoundaryYmd || !this.timelineDates.length) {
      return;
    }
    this.loadAnomaliesRange();
  }

  /** Loads every anomaly polygon for the season once, then groups them by day client-side. */
  private loadAnomaliesRange(): void {
    const fieldId = this.fieldEntity?.id;
    const start = this.anomalyRangeStartYmd;
    this.anomaliesByDay = new Set();
    this.anomaliesByDayRows = new Map();
    if (!fieldId || !start) {
      this.refreshAnomaliesForDay();
      this.cdr.markForCheck();
      return;
    }
    this.anomaliesLoading = true;
    this.cdr.markForCheck();
    this.anomalyService
      .satelliteAnomalyPolygonsCustomGetAnomalyPolygonsPost({
        filters: [
          { filterField: 'fieldId', filterOperator: FilterOperator.Equals, filterValue: fieldId },
          { filterField: 'originalDate', filterOperator: FilterOperator.GreaterThanOrEqual, filterValue: start },
        ],
        take: 3000,
        offset: 0,
      } as any)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([])),
        finalize(() => {
          this.anomaliesLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((rows: any[]) => {
        const byDay = new Set<string>();
        const byDayRows = new Map<string, any[]>();
        for (const row of rows || []) {
          const dt = row?.originalDate ?? row?.anomalyPolygon?.originalDate;
          const ymd = ymdFromApiDate(dt);
          if (!ymd) {
            continue;
          }
          byDay.add(ymd);
          const list = byDayRows.get(ymd);
          if (list) {
            list.push(row);
          } else {
            byDayRows.set(ymd, [row]);
          }
        }
        this.anomaliesByDay = byDay;
        this.anomaliesByDayRows = byDayRows;
        this.loadFieldAnomalyDictionary(extractAnomalyCodesFromRows(rows || []));
        this.refreshAnomaliesForDay();
        this.cdr.markForCheck();
      });
  }

  /** Picks the cached anomalies for the selected day (no extra request). */
  private refreshAnomaliesForDay(): void {
    const ymd = this.selectedTimelineDate ? dateToYmd(this.selectedTimelineDate) : null;
    this.anomaliesForSelectedDay = ymd ? this.anomaliesByDayRows.get(ymd) ?? [] : [];
    this.syncRecommendationsForSelectedDay();
    this.cdr.markForCheck();
  }

  private loadFieldAnomalyDictionary(codes: number[]): void {
    this.fieldDetailsRecommendations
      .loadFieldAnomaliesByCode$(codes)
      .pipe(takeUntil(this.destroy$))
      .subscribe(map => {
        this.fieldAnomalyByCode = map;
        this.syncRecommendationsForSelectedDay();
        this.cdr.markForCheck();
      });
  }

  private syncRecommendationsForSelectedDay(): void {
    this.recommendationsForSelectedDay = buildRecommendationsForAnomalyRows(
      this.anomaliesForSelectedDay,
      this.fieldAnomalyByCode,
    );
  }

  private loadCropPhases(): void {
    const cropId = this.selectedCropId;
    if (!cropId) {
      this.cropPhases = [];
      return;
    }
    this.cropPeriodService
      .dictionariesCropPeriodGetByQueryPost({
        filters: [
          { filterField: 'cropId', filterOperator: FilterOperator.Equals, filterValue: cropId },
        ],
        take: 200,
        offset: 0,
      } as any)
      .pipe(takeUntil(this.destroy$), catchError(() => of([])))
      .subscribe(rows => {
        this.cropPhases = (rows || []).slice().sort((a: any, b: any) => (a.dayStart ?? 0) - (b.dayStart ?? 0));
        this.cdr.markForCheck();
      });
  }

  private loadAgroMeasures(): void {
    const cropId = this.selectedCropId;
    if (!cropId) {
      this.agroMeasures = [];
      return;
    }
    this.agroMeasureService
      .dictionariesAgrotechnicalMeasureGetByQueryPost({
        filters: [
          { filterField: 'cropId', filterOperator: FilterOperator.Equals, filterValue: cropId },
        ],
        take: 500,
        offset: 0,
      } as any)
      .pipe(takeUntil(this.destroy$), catchError(() => of([])))
      .subscribe(rows => {
        this.agroMeasures = rows || [];
        this.cdr.markForCheck();
      });
  }

  private chartCacheKey(): string {
    const crop = this.selectedCropId || '_';
    return `${this.selectedIndexValue}::${crop}`;
  }

  private reloadChart(useCache = true): void {
    this.initChartOptionsShell();
    const season = this.selectedSeason;
    if (!season?.id) {
      this.chartData = null;
      this.chartIndexLowSignalYmd = new Set();
      return;
    }
    const idx = this.selectedIndexValue;
    const key = this.chartCacheKey();
    if (useCache) {
      const hit = this.chartSeriesCache.get(key);
      if (hit?.chartData) {
        this.chartData = hit.chartData;
        const cached = hit.chartIndexLowSignalYmd;
        this.chartIndexLowSignalYmd = new Set(cached ?? []);
        this.chartLoading = false;
        return;
      }
    }
    this.chartLoading = true;
    this.indexDataManager
      .loadIndexData(season.id, idx, idx)
      .then(async res => {
        this.chartLoading = false;
        if (!res) {
          this.chartData = null;
          this.chartIndexLowSignalYmd = new Set();
          return;
        }
        const indexLabels = [...res.chartLabels];
        this.chartIndexLowSignalYmd = buildIndexLowSignalYmdSet(indexLabels, res.data);
        const normDataset = await this.buildNormDataset(indexLabels, idx);
        const plot = filterIndexChartSeriesForPlot(indexLabels, res.data);
        const labels = plot.chartLabels.map(d => formatDateLabelShortYear(d));
        const lineColor = res.color || '#2563eb';
        const lineData = plot.values;
        const datasets: any[] = [
          {
            label: idx,
            data: lineData,
            borderColor: lineColor,
            backgroundColor: lineColor + '33',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            spanGaps: true,
            order: 0,
          },
        ];
        if (normDataset) {
          datasets.push({
            label: normDataset.label,
            data: interpolateSeriesToLabels(plot.chartLabels, normDataset.ownLabels ?? [], normDataset.data),
            borderColor: normDataset.borderColor,
            backgroundColor: 'transparent',
            borderDash: normDataset.borderDash ?? [4, 4],
            borderWidth: 2,
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            spanGaps: true,
            order: 1,
          });
        }
        this.chartData = {
          labels,
          datasets,
        };
        this.chartSeriesCache.set(key, {
          chartData: this.chartData,
          chartIndexLowSignalYmd: new Set(this.chartIndexLowSignalYmd),
        });
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.chartLoading = false;
        this.chartData = null;
        this.chartIndexLowSignalYmd = new Set();
      });
  }

  private initChartOptionsShell(): void {
    this.chartOptions = buildFieldDetailsLineChartOptions();
  }

  /** Fetch WMS preview image for the current bbox, timeline date, and index. */
  /**
   * Resolve the GeoServer layer name for the selected index/date and build an XYZ tile URL template
   * ({x}/{y}/{z}) consumed by the OpenLayers mini-map (`/tiles/Map/GetTileByLayer`).
   */
  private refreshWmsOverlay(): void {
    this.revokeWms();
    const date = this.selectedTimelineDate;
    const idx = this.selectedIndexValue || 'Ndvi';
    if (!this.fieldPolygonJson || !date) {
      this.indexTileUrl = null;
      this.cdr.markForCheck();
      return;
    }
    const seq = ++this.wmsLoadSeq;
    this.wmsTileLoading = true;
    this.cdr.markForCheck();
    const stylesParam = wmsStyleForIndex(idx);
    this.indexManager
      .loadAvailableIndices(date, idx)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
        finalize(() => {
          if (seq === this.wmsLoadSeq) {
            this.wmsTileLoading = false;
            this.cdr.markForCheck();
          }
        }),
      )
      .subscribe((layers: any[] | null) => {
        if (seq !== this.wmsLoadSeq) {
          return;
        }
        const geo = layers?.[0]?.geoServerName;
        const layerBase = geo ? String(geo).trim().replace(/^indexint8:/i, '').trim() : '';
        if (!layerBase) {
          this.indexTileUrl = null;
          this.cdr.markForCheck();
          return;
        }
        const layersParam = `indexint8:${layerBase}`;
        this.indexTileUrl =
          `${environment.api}/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}` +
          `&layer=${encodeURIComponent(layersParam)}&style=${encodeURIComponent(stylesParam)}&coordinateSystem=0`;
        this.cdr.markForCheck();
      });
  }

  /**
   * Last finished bid for the field with a result file: download ZIP and show thumbnails.
   */
  private loadFieldPhotosFromLastFinishedBid(): void {
    this.resetFieldPhotosUi();
    const fieldId = this.fieldEntity?.id ?? this.field?.id;
    if (!fieldId) {
      this.fieldPhotosLoading = false;
      this.fieldPhotosProgress = 0;
      this.cdr.markForCheck();
      return;
    }
    this.fieldPhotosLoading = true;
    this.fieldPhotosProgress = 0;
    this.cdr.markForCheck();

    this.bidService
      .cropMonitoringBidGetByQueryPost(buildFieldPhotoBidsQuery(String(fieldId)))
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as unknown[])))
      .subscribe(bids => {
        const fileResultId = pickLatestFieldPhotoBidFileResultId(bids, FIELD_PHOTOS_EMPTY_GUID);
        if (!fileResultId) {
          this.fieldPhotosLoading = false;
          this.fieldPhotosProgress = 0;
          this.cdr.markForCheck();
          return;
        }
        this.downloadFieldPhotosZip(fileResultId);
      });
  }

  private downloadFieldPhotosZip(fileResultId: string): void {
    this.fieldPhotosProgress = 0;
    this.notReadyApi
      .downloadFileWithProgress(fileResultId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (event: any) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              this.fieldPhotosProgress = fieldPhotosHttpProgressPercent(event.loaded, event.total);
            }
            this.cdr.markForCheck();
          } else if (event.type === HttpEventType.Response) {
            this.fieldPhotosProgress = 85;
            this.cdr.markForCheck();
            const blob = event.body as Blob;
            if (!(blob instanceof Blob) || blob.size === 0) {
              this.finishFieldPhotosLoad(false);
              return;
            }
            try {
              const map = await ZipExtractor.extractToMemory(blob, p => {
                this.fieldPhotosProgress = fieldPhotosZipExtractOverlayPercent(p);
                this.cdr.markForCheck();
              });
              const images = extractZipImageGalleryItems(map);
              images.forEach(({ src, name }) => this.photoBlobUrls.set(name, src));
              this.photosTotal = images.length;
              this.photoGalleryUrls = images.map(x => x.src);
              this.photoStrip = images.slice(0, 3);
              this.fieldPhotosProgress = 100;
            } catch (e) {
              handleErrors(e);
              this.finishFieldPhotosLoad(false);
              return;
            }
            this.finishFieldPhotosLoad(true);
          }
        },
        error: () => {
          this.finishFieldPhotosLoad(false);
        },
      });
  }

  private finishFieldPhotosLoad(ok: boolean): void {
    this.fieldPhotosLoading = false;
    if (!ok) {
      this.fieldPhotosProgress = 0;
      this.cleanupPhotoBlobs();
      this.photoStrip = [];
      this.photoGalleryUrls = [];
      this.photosTotal = 0;
      this.photoSliderImages = [];
    }
    this.cdr.markForCheck();
  }

  private async buildNormDataset(
    chartLabels: string[],
    selectedIndexValue: string,
  ): Promise<{ label: string; data: (number | null)[]; borderColor: string; borderDash?: number[]; ownLabels?: string[] } | null> {
    const cropId = this.selectedCropId || this.selectedSeason?.cropId || null;
    const plantingDate = this.plantingBoundaryYmd ?? undefined;
    const vegType = INDEX_TO_VEGETATION_TYPE[selectedIndexValue];
    if (!cropId || !vegType) {
      return null;
    }
    const normData = await this.normDataManager.loadNormData(vegType, cropId);
    const norm = this.normDataManager.createNormDataset(
      normData,
      chartLabels,
      plantingDate,
      selectedIndexValue.toUpperCase(),
      selectedIndexValue,
      this.cropPhases,
    );
    if (!norm) {
      return null;
    }
    return {
      label: norm.label,
      data: norm.data,
      borderColor: norm.borderColor,
      borderDash: norm.borderDash,
      ownLabels: norm.ownLabels,
    };
  }

  private revokeWms(): void {
    this.wmsLoadSeq++;
    this.wmsTileLoading = false;
    this.indexTileUrl = null;
  }

  private cleanupPhotoBlobs(): void {
    this.photoBlobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* noop */
      }
    });
    this.photoBlobUrls.clear();
    this.photoStrip = [];
    this.photoGalleryUrls = [];
    this.photosTotal = 0;
  }

  /** Clears in-memory photo blobs and UI before a new ZIP / bid load. */
  private resetFieldPhotosUi(): void {
    this.photoSliderVisible = false;
    this.cleanupPhotoBlobs();
    this.photoSliderImages = [];
  }
}
